import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CustomToggle from 'components/CustomToggle/CustomToggle';
import { BodyM } from 'components/Typography/Typography';
import MobilePrepaid from './subcomponents/MobilePrepaid';
import MobilePostpaid from './subcomponents/MobilePostpaid';
import Fix from './subcomponents/Fix';
import { decode } from 'utils/crypto/crypto';
import { translate } from 'components/Translation';
import invalidateCache from 'utils/api/invalidateCache';
import { finalize, concatMap, catchError } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { UsersService } from 'services/users/users';
import { AccountsService } from 'services/accounts/accounts';
import { ProductsService } from 'services/products/products';
import SimpleFailureDialog from 'views/Numbers/Details/common/SimpleFailureDialog';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/servicesStyle';
import { makeStyles } from '@material-ui/core';
import { getProductCharacteristic } from 'views/Numbers/helpers/getters';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { CustomerAccountResponse } from 'utils/api/models/accounts/customerAccountResponse';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ServicesConfirmationDialog from './subcomponents/ServicesConfirmationDialog';
import { Package } from 'utils/api/models/numbers/usage';
import FBB from './subcomponents/FBB/';
import { NumbersService } from 'services/numbers/numbers';
import { SingleErrorMessage } from 'utils/api/models/errors/singleErrorMessage';
import { getLanguage } from 'utils/language/language';
import { SingleOrderStatus } from 'utils/api/models/orderTracking/singleOrderStatus';
import RefreshStatusesCard from './subcomponents/common/RefreshStatusesCard';
import UpdateServicesConfirmationDialog from './subcomponents/common/UpdateServicesConfirmationDialog';
import { AccountType } from 'utils/api/models/accounts/accountType';
import { SubscriberProductsService } from 'services/subscriberProducts/subscriberProducts';
import { SingleSubscriberProduct } from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { OrderTrackingService } from 'services/orderTracking/orderTracking';
import { TagManager } from 'commons/gtm/tagManager';
import { upIdSelector, userIdSelector } from 'redux/modules/session/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IServicesSubpageProps {
	match: {
		path: string;
		params: {
			phoneNumberEncoded: string;
			accountType: string;
			billingType: string;
			accountNumber: string;
			upAccountId: string;
		};
	};
	accountName: string;
}

const Services = ({ match, accountName }: IServicesSubpageProps) => {
	const classes = useStyles();
	const { phoneNumberEncoded, accountType, billingType, accountNumber, upAccountId } = match.params;
	const phoneNumber: string = decode(phoneNumberEncoded);
	const [loadingSubscriberProducts, setLoadingSubscriberProducts] = useState<boolean>(true);
	const [loadingOrderStatuses, setLoadingOrderStatuses] = useState<boolean>(true);
	const [loadingCustomerAccount, setLoadingCustomerAccount] = useState<boolean>(true);
	const [loadingPackages, setLoadingPackages] = useState<boolean>(true);
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
	const [updatingServices, setUpdatingServices] = useState<boolean>(true);
	const [serviceData, setServiceData] = useState<types.services.IServiceData | null>(null);
	const [userData, setUserData] = useState<CorpUserData | undefined>();
	const [customerAccount, setCustomerAccount] = useState<CustomerAccountResponse | null>(null);
	const [showOnlyActive, setShowOnlyActive] = useState<boolean>(false);
	const [isFailureDialogOpen, setIsFailureDialogOpen] = useState<boolean>(false);
	const [errorText, setErrorText] = useState<string | undefined>(undefined);
	const [servicesErrorMessages, setServicesErrorMessages] = useState<SingleErrorMessage[]>([]);
	const [tariffPlan, setTariffPlan] = useState<SingleSubscriberProduct | undefined>();
	const [orderStatuses, setOrderStatuses] = useState<SingleOrderStatus[]>([]);
	const [packages, setPackages] = useState<Package[]>([]);
	const [subscriberProducts, setSubscriberProducts] = useState<SingleSubscriberProduct[]>([]);
	const [showRefreshStatusNotification, setShowRefreshStatusNotification] = useState<boolean>(false);
	const [documentId, setDocumentId] = useState<string>('');
	const isMobile: boolean = accountType === 'MOBILE';

	const lang = getLanguage();
	const dispatch = useDispatch();
	const isMounted = useRef(true);

	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);

	const loading: boolean =
		loadingCustomerAccount || loadingSubscriberProducts || loadingPackages || loadingOrderStatuses;

	const getContent = () => {
		if (billingType === 'PREPAID' && accountType === 'MOBILE') {
			return <MobilePrepaid subscriberProducts={subscriberProducts} showOnlyActive={showOnlyActive} />;
		}
		if (billingType === 'POSTPAID' && accountType === 'MOBILE') {
			return (
				<MobilePostpaid
					showOnlyActive={showOnlyActive}
					phoneNumber={phoneNumber}
					accountType={accountType}
					billingType={billingType}
					accountNumber={accountNumber}
					updateOrderItems={updateOrderItems}
					userData={userData}
					packages={packages}
					tariffPlan={tariffPlan}
					subscriberProducts={subscriberProducts}
					orderStatuses={orderStatuses}
					documentId={documentId}
				/>
			);
		}
		if (accountType === 'INTERNET') {
			return (
				<FBB
					subscriberProducts={subscriberProducts}
					showOnlyActive={showOnlyActive}
					accountName={accountName}
					match={match}
					updateOrderItems={updateOrderItems}
				/>
			);
		}
		if (accountType === 'FIX') {
			return (
				<Fix
					subscriberProducts={subscriberProducts}
					showOnlyActive={showOnlyActive}
					updateOrderItems={updateOrderItems}
				/>
			);
		}
		return (
			<div className={classes.noServices}>
				<BodyM disableMargin>{translate('numbers.services.no.services.available')}</BodyM>
			</div>
		);
	};

	const hideFailureDialog = () => {
		setErrorText(undefined);
		setIsFailureDialogOpen(false);
	};

	const showFailureDialog = () => {
		setIsFailureDialogOpen(true);
	};

	const getEbuConfigProducts = (orderItems: types.services.IOrderItem[]) => {
		return [...new Set(orderItems.map((orderItem) => orderItem.ebuConfigProduct))].filter(Boolean);
	};

	const parseOrderStatuses = (rawStatuses: SingleOrderStatus[]): SingleOrderStatus[] => {
		const parsedStatuses: SingleOrderStatus[] = [];
		const filteredRawStatuses = rawStatuses.filter(
			(e) => e.productId !== null && e.status !== null && e.status !== 'Order Completed'
		);
		filteredRawStatuses.forEach((singleRawStatus) => {
			const productIds = singleRawStatus.productId.split(',');
			for (const productId of productIds) {
				parsedStatuses.push({
					...singleRawStatus,
					productId,
				});
			}
		});
		return parsedStatuses;
	};

	const prepareServiceIds = (orderItems: types.services.IOrderItem[]) => {
		const idsToAdd = orderItems.filter(({ action }) => action === 'ADD').map(({ id }) => id);
		const idsToRemove = orderItems.filter(({ action }) => action === 'REMOVE').map(({ id }) => id);
		const serviceOrderItems: types.services.IServiceItem[] = [];
		if (idsToAdd.length > 0) {
			serviceOrderItems.push({
				action: 'ADD',
				productIds: idsToAdd,
			});
		}
		if (idsToRemove.length > 0) {
			serviceOrderItems.push({
				action: 'REMOVE',
				productIds: idsToRemove,
			});
		}
		return serviceOrderItems;
	};

	const getErrorLabel = (key: string) => {
		return servicesErrorMessages.find((singleErrorMessage) => singleErrorMessage.key === key)?.label;
	};

	const setDetailedErrorDescription = (
		code: string,
		detailedCode: string,
		orderItems: types.services.IServiceItem[]
	) => {
		let label: string | undefined;
		const productIds: string[] = [];
		// concatenate all product ids
		for (const singleOrderItem of orderItems) {
			productIds.push(...singleOrderItem.productIds);
		}
		// I don't know which service failed so I check the whole array od IDs.
		// Most activations have only one product, I didn't see more than two.
		for (const productId of productIds) {
			const id = productId.replace(/\D/g, '');
			const key = `error.${id}.${detailedCode}`;
			label = getErrorLabel(key);
			// if label found
			if (label) {
				break;
			}
		}
		// if there is no error assigned for ID we search by detailed code
		if (!label) {
			label = getErrorLabel(`error.${detailedCode}`);
		}
		// if there is no error assigned for detailed code we search by code
		if (!label) {
			label = getErrorLabel(`error.${code}`);
		}
		// if there is no error assigned for code we use default value
		if (!label) {
			label = getErrorLabel('error.DEFAULT');
		}
		setErrorText(label);
	};

	const submitServices = (
		ebuConfigProduct: string,
		orderItems: types.services.IServiceItem[],
		action: types.services.actionType,
		serviceName: string,
		asynchronousActivation?: boolean
	) => {
		setServiceData({ action, serviceName, asynchronousActivation: !!asynchronousActivation });
		setUpdatingServices(true);
		setConfirmationDialogOpen(true);
		ProductsService.submitServices({
			phoneNumber: phoneNumber,
			ebuConfigProduct: ebuConfigProduct,
			orderItems: orderItems,
			documentId,
		}).subscribe(
			({ returnCode }) => {
				if (parseInt(returnCode) === 0) {
					invalidateCache(['getCustomerProducts', 'getUsage', 'getOrderStatus'], true);
					fetchData();
				} else {
					hideConfirmationDialog();
					showFailureDialog();
				}
			},
			({ response }) => {
				const { code, detailedCode } = response;
				setDetailedErrorDescription(code, detailedCode, orderItems);
				hideConfirmationDialog();
				showFailureDialog();
			}
		);
	};

	const createServices = (
		orderItems: types.services.IOrderItem[],
		action: types.services.actionType,
		serviceName: string,
		asynchronousActivation?: boolean
	) => {
		const _customerAccount = customerAccount?.customerAccount;
		if (_customerAccount) {
			setServiceData({ action, serviceName, asynchronousActivation: !!asynchronousActivation });
			setUpdatingServices(true);
			setConfirmationDialogOpen(true);
			const accountExternalId = getProductCharacteristic(_customerAccount, 'ACCOUNT_EXTERNAL_ID');
			const subscriberNumber = getProductCharacteristic(_customerAccount, 'SUBSCRIBER_NUMBER');
			const accountNumber = getProductCharacteristic(_customerAccount, 'ACCOUNT_NO');
			const subscriberNumberResets = getProductCharacteristic(_customerAccount, 'SUBSCRIBER_NUMBER_RESETS');
			ProductsService.updateServices({
				phoneNumber,
				accountExternalId,
				subscriberNumber,
				accountNumber,
				subscriberNumberResets,
				accountId: userData?.accountId || '',
				orderItems: orderItems,
				documentId,
				otp: '',
			}).subscribe(
				({ success }) => {
					if (success) {
						invalidateCache(['getCustomerProducts', 'getUsage', 'getOrderStatus'], true);
						fetchData();
					} else {
						hideConfirmationDialog();
						showFailureDialog();
					}
				},
				(e) => {
					if (e?.response?.code === 'MULTI_ACTIVATION_OF_PRODUCT_FORBIDDEN') {
						setErrorText(translate('number.services.error.multiactivate'));
					}
					hideConfirmationDialog();
					showFailureDialog();
				}
			);
		} else {
			showFailureDialog();
		}
	};

	const updateOrderItems: types.services.updateOrderItemsFunction = (
		orderItems: types.services.IOrderItem[],
		action: types.services.actionType,
		serviceName: string,
		asynchronousActivation?: boolean
	) => {
		const ebuConfigProducts = getEbuConfigProducts(orderItems);
		if (ebuConfigProducts.length === 1 && !!ebuConfigProducts[0]) {
			// the same type of config for every service item
			const serviceOrderItems = prepareServiceIds(orderItems);
			submitServices(ebuConfigProducts[0], serviceOrderItems, action, serviceName, asynchronousActivation);
		} else {
			// different types of config or config not set for the selected service items
			createServices(orderItems, action, serviceName, asynchronousActivation);
		}
	};

	const fetchData = useCallback(
		(showNotification?: boolean) => {
			dispatch(showLoadingIndicator());
			const getUsageProductsResponse = SubscriberProductsService.getSubscriberProducts(
				phoneNumber,
				billingType,
				accountType
			).pipe(
				concatMap(({ products, tariffPlan }) => {
					if (isMounted.current) {
						setSubscriberProducts(products);
						setTariffPlan(tariffPlan(accountType as AccountType));
						return NumbersService.getUsage({
							phoneNumber,
							billingType,
							accountType,
						});
					}
					setUpdatingServices(false);
					dispatch(hideLoadingIndicator());
					return EMPTY;
				}),
				catchError((error) => {
					setUpdatingServices(false);
					dispatch(hideLoadingIndicator());
					return throwError(error);
				})
			);
			const getOrderStatusesResponse = getUsageProductsResponse.pipe(
				concatMap(({ packages }) => {
					if (isMounted.current) {
						setPackages(packages);
						if (documentId) {
							return OrderTrackingService.getOrderStatus(accountNumber, documentId, phoneNumber);
						}
					}
					setUpdatingServices(false);
					dispatch(hideLoadingIndicator());
					return EMPTY;
				}),
				catchError((error) => {
					setUpdatingServices(false);
					dispatch(hideLoadingIndicator());
					return throwError(error);
				})
			);
			getOrderStatusesResponse
				.pipe(
					finalize(() => {
						if (isMounted.current) {
							setUpdatingServices(false);
							dispatch(hideLoadingIndicator());
						}
					})
				)
				.subscribe(
					(response) => {
						if (isMounted.current) {
							setOrderStatuses(parseOrderStatuses(response.orderStatuses));
							if (showNotification) {
								setShowRefreshStatusNotification(true);
							}
						}
					},
					() => {
						setUpdatingServices(false);
						dispatch(hideLoadingIndicator());
					}
				);
		},
		[phoneNumber, dispatch, billingType, accountType, documentId, accountNumber]
	);

	const hideConfirmationDialog = () => {
		setConfirmationDialogOpen(false);
		setServiceData(null);
		setUpdatingServices(false);
	};

	const pendingStatuses: boolean = useMemo(() => {
		return subscriberProducts.some(({ productId, status }) => !!productId && status !== 'Active');
	}, [subscriberProducts]);

	const showRefreshStatusesCard = !loading && (pendingStatuses || orderStatuses.length > 0) && isMobile;

	useEffect(() => {
		setLoadingSubscriberProducts(true);
		SubscriberProductsService.getSubscriberProducts(phoneNumber, billingType, accountType)
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoadingSubscriberProducts(false);
					}
				})
			)
			.subscribe(
				({ products, tariffPlan }) => {
					if (isMounted.current) {
						setSubscriberProducts(products);
						setTariffPlan(tariffPlan(accountType as AccountType));
					}
				},
				(error) => {
					setLoadingSubscriberProducts(false);
					throwError(error);
				}
			);
	}, [phoneNumber, accountType, billingType]);

	useEffect(() => {
		if (documentId) {
			setLoadingOrderStatuses(true);
			OrderTrackingService.getOrderStatus(accountNumber, documentId, phoneNumber)
				.pipe(
					finalize(() => {
						if (isMounted.current) {
							setLoadingOrderStatuses(false);
						}
					})
				)
				.subscribe(
					(response) => {
						setOrderStatuses(parseOrderStatuses(response.orderStatuses));
					},
					() => {
						if (isMounted.current) {
							setLoadingOrderStatuses(false);
						}
					}
				);
		}
	}, [documentId, accountNumber, phoneNumber]);

	useEffect(() => {
		UsersService.getCorpUserDepartmentDataForNumber(upAccountId).subscribe((data) => {
			if (isMounted.current) {
				setUserData(data);
			}
		});
	}, [upAccountId]);

	useEffect(() => {
		setLoadingPackages(true);
		NumbersService.getUsage({
			phoneNumber,
			billingType,
			accountType,
		})
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoadingPackages(false);
					}
				})
			)
			.subscribe(
				(data) => {
					if (isMounted.current) {
						setPackages(data.packages);
						setUpdatingServices(false);
					}
				},
				(error) => {
					setLoadingPackages(false);
					throwError(error);
				}
			);
	}, [phoneNumber, billingType, accountType]);

	useEffect(() => {
		if (userData) {
			AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
				const _documentId = accounts.find(({ accountId }) => accountId === userData.accountId)?.documentId;
				setDocumentId(String(_documentId));
			});
		}
	}, [userData]);

	useEffect(() => {
		setLoadingCustomerAccount(true);
		AccountsService.getCustomerAccountOperation({
			phoneNumber,
			accountType,
		})
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoadingCustomerAccount(false);
					}
				})
			)
			.subscribe(
				(data) => {
					if (isMounted.current) {
						setCustomerAccount(data);
					}
				},
				() => {
					if (isMounted.current) {
						setLoadingCustomerAccount(false);
					}
				}
			);
	}, [phoneNumber, accountType]);

	useEffect(() => {
		// in the background
		NumbersService.getErrorMessages().subscribe(({ errorMessages }) => {
			const filterByLang = errorMessages.filter((singleErrorMessage) => singleErrorMessage.lang === lang);
			setServicesErrorMessages(filterByLang);
		});
	}, [lang]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		if (!loading) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loading, dispatch]);

	React.useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: `My Numbers: Services`,
				accountID: accId,
				billingID: match.params.accountNumber,
				accountNumber: phoneNumber,
				userId: userId,
				language: getLanguage(),
			},
		});
	}, []);
	return (
		<div>
			<BreadcrumbsItem to={match.path}>{`${phoneNumber} (${accountName}) - ${translate(
				'path.services'
			)}`}</BreadcrumbsItem>
			<div className={classes.header}>
				<CustomToggle
					value="showOnlyActive"
					checked={showOnlyActive}
					onChange={(event: React.FormEvent<HTMLInputElement>) =>
						setShowOnlyActive((event.target as HTMLInputElement).checked)
					}
					label={translate('show.only.active')}
					formControlProps={{ className: classes.switch }}
				/>
			</div>
			<ConditionalRender
				show={showRefreshStatusesCard}
				onTrue={() => <RefreshStatusesCard fetchData={fetchData} />}
			/>
			<ConditionalRender
				show={!loading}
				onTrue={() => (
					<Card className={classes.card}>
						<CardBody className={classes.cardBody}>{getContent()}</CardBody>
					</Card>
				)}
			/>
			<ServicesConfirmationDialog
				isOpen={confirmationDialogOpen}
				hideDialog={() => setConfirmationDialogOpen(false)}
				serviceData={serviceData}
				updatingServices={updatingServices}
			/>
			<SimpleFailureDialog isOpen={isFailureDialogOpen} hideDialog={hideFailureDialog} text={errorText} />
			<UpdateServicesConfirmationDialog
				isOpen={showRefreshStatusNotification}
				hideDialog={() => setShowRefreshStatusNotification(false)}
			/>
		</div>
	);
};

export default Services;
